// ui/src/components/padrekko/qc/HandoffWarningsModal.jsx
//
// The acknowledgment step of the soft handoff gate.
//
// TWO WARNINGS, ONE MOMENT. Findings nobody dispositioned, and pads nobody
// opened. They are different quantities and are never added together or put in
// the same sentence — a case can have every pad completed and no finding
// decided, or every finding decided on the two pads anyone opened.
//
// ── IT ASKS, IT DOES NOT REFUSE ─────────────────────────────────────────────
//
// Proceeding is always available. A hard gate on a large engagement is what
// generates pressure for a bulk-validate control, and that control would
// hollow out the claim validation exists to make. What this buys instead is a
// record: the count goes into the case history under the acknowledger's name,
// so a skip is on the record rather than silently absent.
//
// ── THE WORD IS "DISPOSITIONED" ─────────────────────────────────────────────
//
// The gate proves every finding was CONSIDERED — someone at this stage
// validated it or did work on it. It proves nothing about assent: a revised
// finding is the reviewer's version rather than their endorsement of what was
// there, and a removed finding never reaches the next stage at all. Words in
// the assent family are a stronger claim than the data supports and are barred
// from this surface by dispositionGate.test.mjs, which scans the copy.
//
// The word `reviewed` is also out, for a different reason: it is the name of a
// specific counter with exactly one definition, and reusing it here for a
// slightly different question is how that word came to mean six things before.

import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

export default function HandoffWarningsModal({
  isOpen,
  detail,
  action = "forward",
  busy = false,
  onConfirm,
  onClose,
}) {
  const [showList, setShowList] = useState(false);
  if (!isOpen || !detail) return null;

  const warnings = detail.warnings ?? [];
  const disposition = warnings.find((w) => w.code === "undispositioned_findings");
  const skipped = warnings.find((w) => w.code === "majority_skipped");
  const unverified = warnings.find((w) => w.code === "unverified_closeouts");
  const findings = disposition?.findings ?? [];
  const pending = disposition?.undispositioned_count ?? 0;

  // ── A CODE THIS FILE HAS NEVER HEARD OF ─────────────────────────────────
  //
  // Every warning used to be picked out by name, so a code the server added
  // and this modal did not know about rendered NOTHING — the user was asked to
  // acknowledge something they could not see, and the acknowledgment still
  // went into the case history under their name as though they had.
  //
  // That is the rail's empty-row bug in a worse place: there, an unknown event
  // produced a blank line; here, it produces a consent nobody gave. Same
  // cause — a list of known cases with no honest default.
  //
  // So anything not rendered above falls through to its own `detail` string,
  // which the server already writes as a sentence. A fourth code degrades to
  // something visible instead of to silence.
  const KNOWN = new Set([
    "undispositioned_findings", "majority_skipped", "unverified_closeouts",
  ]);
  const unknown = warnings.filter((w) => !KNOWN.has(w.code));
  const verb = action === "close" ? "Close case" : "Forward anyway";

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Undispositioned findings"
    >
      <div
        className="w-full max-w-lg bg-bg-soft border border-slate-700 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-800">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-slate-100">
              {warnings.length > 1 ? "Before you hand this over" : "Before you hand this over"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel"
            className="shrink-0 p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-3 space-y-3">
          {/* TWO DIFFERENT QUANTITIES, LABELLED AS SUCH.
              Pads skipped is coverage of the survey; findings undispositioned
              is judgment on what the survey found. A case can have every pad
              completed and no finding decided, or every finding decided on the
              two pads anyone opened — so they are never added together and
              never share a sentence. */}
          {disposition && (
            <div>
              <p className="text-[13px] text-slate-200">
                <span className="font-semibold tabular-nums">
                  {disposition.undispositioned_count} of {disposition.active_count}
                </span>{" "}
                findings have not been dispositioned at this stage.
              </p>
              <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">
                A finding is dispositioned when someone at this stage validated it
                or revised it. Seeing a finding, adding a note, or sending it back
                does not count — those defer or annotate rather than decide.
              </p>
            </div>
          )}

          {skipped && (
            <div>
              <p className="text-[13px] text-slate-200">
                <span className="font-semibold tabular-nums">
                  {skipped.pads_skipped} of {skipped.pads_terminal}
                </span>{" "}
                pads were skipped.
              </p>
              <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">
                A skipped pad was never opened, so nothing on it was looked at —
                a separate question from whether the findings that were surfaced
                have been decided.
              </p>
            </div>
          )}

          {unverified && (
            <div>
              <p className="text-[13px] text-slate-200">
                <span className="font-semibold tabular-nums">
                  {unverified.unverified_count}
                </span>{" "}
                {unverified.unverified_count === 1 ? "finding was" : "findings were"} closed out
                with no verification recorded.
              </p>
              <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">
                The operator reported the work done and nobody has ruled on it
                yet. Completing the case makes it read-only, so the verdict
                cannot be recorded afterwards.
              </p>
            </div>
          )}

          {unknown.map((w) => (
            <div key={w.code}>
              <p className="text-[13px] text-slate-200">{w.detail}</p>
              <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">
                This warning is newer than this screen. It is shown as the
                server wrote it rather than hidden.
              </p>
            </div>
          ))}

          <p className="text-[12px] text-slate-400 leading-relaxed">
            You can continue. The count will be recorded in the case history
            against your name, alongside the stage and the time.
          </p>

          {findings.length > 0 && (
            <div>
              {/* Not listed by default: thirty-one is fine, six hundred is not.
                  Available on request, which is the difference between a
                  warning a reader can act on and one they can only accept. */}
              <button
                type="button"
                onClick={() => setShowList((v) => !v)}
                className="text-[11px] text-slate-400 hover:text-slate-200 underline decoration-dotted underline-offset-2"
              >
                {showList ? "Hide the list" : `Show which ${pending}`}
              </button>
              {showList && (
                <ul className="mt-2 max-h-48 overflow-y-auto rounded border border-slate-800 divide-y divide-slate-800/70">
                  {findings.map((f) => (
                    <li
                      key={`${f.pad_name}-${f.idx}`}
                      className="px-2 py-1 text-[11px] text-slate-300 flex items-center gap-2"
                    >
                      <span className="text-slate-500 truncate">{f.pad_name}</span>
                      <span className="font-mono text-slate-400">#{f.idx}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded border border-amber-500/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25 disabled:opacity-50"
          >
            {busy ? "Working…" : verb}
          </button>
        </footer>
      </div>
    </div>
  );
}
